"use client";
import Image from "next/image";
import { motion, useAnimation } from "framer-motion";
import { mediaFeatures } from "@/data/mediaFeatures";
import { useEffect } from "react";
import { FaExternalLinkAlt } from "react-icons/fa";

const CarbonBackground = () => {
    const controls = useAnimation();

    useEffect(() => {
        const animate = async () => {
            while (true) {
                await controls.start({
                    x: [0, -100, 0],
                    y: [0, 50, 0],
                    transition: { duration: 60, ease: "linear" }
                });
            }
        };
        animate();
    }, [controls]);

    return (
        <div className="absolute inset-0 overflow-hidden opacity-5">
            {[...Array(20)].map((_, i) => (
                <motion.div
                    key={i}
                    animate={controls}
                    className="absolute bg-primary rounded-full"
                    style={{
                        width: Math.random() * 10 + 2,
                        height: Math.random() * 10 + 2,
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                    }}
                />
            ))}
        </div>
    );
};


const MediaFeaturesSection = () => {
    return (
        <section  className="relative z-10 bg-gradient-to-b from-[#e8eee7] to-[#c0dab8] py-12 md:py-20 lg:py-24 overflow-hidden">
            <CarbonBackground />
            <div className="relative z-20 container mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <motion.div
                    className="text-center max-w-3xl mx-auto mb-12 lg:mb-16"
                    initial={{ opacity: 0, y: 60 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, type: "spring", bounce: 0.25 }}
                    viewport={{ once: true }}
                >
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                        Featured In & Recognised By
                    </h2>
                    <p className="text-primary-accent">Our mission has been spotlighted by leading voices in sustainability and tech.</p>
                </motion.div>

                {/* Feature Cards Grid */}
                <motion.div
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-7xl mx-auto"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.2, duration: 1 }}
                    viewport={{ once: true }}
                >
                    {mediaFeatures.map((item, index) => (
                        <motion.a
                            key={index}
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block group p-5 sm:p-6 bg-white/40 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 hover:border-primary-accent"
                            whileHover={{ y: -5 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            {/* Image */}
                            <div className="relative aspect-video rounded-lg overflow-hidden mb-4">
                                <Image
                                    src={item.image}
                                    alt={item.title}
                                    fill
                                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                                />
                            </div>

                            {/* Publisher */}
                            <div className="flex items-center gap-3 mb-3">
                                <div className="relative w-6 h-6 flex-shrink-0">
                                    <Image
                                        src={item.logo}
                                        alt={`${item.name} logo`}
                                        fill
                                        className="object-contain"
                                    />
                                </div>
                                <span className="text-xs sm:text-sm font-medium text-gray-600">{item.name}</span>
                                <span className="text-xs text-gray-400 ml-auto">{item.date}</span>
                            </div>

                            {/* Content */}
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                                {item.title}
                            </h3>
                            <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                                {item.excerpt}
                            </p>

                            {/* Read More */}
                            <div className="flex items-center gap-2 text-green-600 group-hover:text-green-700 transition-colors">
                                <span className="text-sm font-medium text-primary-accent hover:text-primary">Read the full article</span>
                                <FaExternalLinkAlt size={12} className="text-primary-accent hover:text-primary" />
                            </div>
                        </motion.a>
                    ))}
                </motion.div>
            </div>
            {/* Glowing background element */}
            <div className="absolute bottom-10 right-10 w-96 h-96 bg-white opacity-10 rounded-full blur-2xl z-0 animate-pulse hidden md:block"></div>
            <div className="absolute bottom-10 left-10 w-64 h-64 bg-white opacity-10 rounded-full blur-2xl z-0 animate-pulse hidden md:block"></div>
            <div className="absolute top-10 left-20 w-96 h-96 bg-primary opacity-10 rounded-full blur-2xl z-0 animate-pulse hidden md:block"></div>
            <div className="absolute top-10 right-40 w-64 h-64  bg-primary opacity-10 rounded-full blur-2xl z-0 animate-pulse hidden md:block"></div>
        </section>
    );
};

export default MediaFeaturesSection;